import React, { useRef } from 'react'
import { Dialog } from '@progress/kendo-react-dialogs'

import PREFERENCE_IMAGE from '@public/assets/time_entry_creation_preference.svg'
import type { GridViewPreference } from '../../../../api/userPreferencesApi'
import {
  TIME_ENTRY_CREATION_OPTIONS,
  TIME_ENTRY_CREATION_QUESTION,
} from '@/features/settings/utils/timeEntryCreationOptions'
import '@css/HeartBeatDialog.css'

type ViewPreferenceDialogProps = {
  error?: string | null
  isSaving?: boolean
  onSelect: (view: GridViewPreference) => void
}


export function ViewPreferenceDialog({
  error,
  isSaving = false,
  onSelect,
}: ViewPreferenceDialogProps) {
  const selectedViewRef = useRef<GridViewPreference>('time')

  return (
    <Dialog
      className="heartbeat-dialog view-preference-dialog [&_.k-dialog-titlebar]:hidden [&_.k-dialog]:!w-[448px] [&_.k-dialog]:!max-w-[calc(100vw-32px)]"
    >
      <div className="heartbeat-card relative bg-white">
        <div className="heartbeat-image h-[240px] overflow-hidden bg-[#F2ECFF]">
          <img
            src={PREFERENCE_IMAGE}
            alt=""
            className="block size-full object-cover"
          />
        </div>

        <div className="px-6 py-6">
          <h1 className="mb-3 mt-0 text-base font-semibold text-[#141414]">
            {TIME_ENTRY_CREATION_QUESTION}
          </h1>

          <div className="grid grid-cols-1 gap-3">
            {TIME_ENTRY_CREATION_OPTIONS.map(option => {
              return (
                <label
                  key={option.value}
                  className={`view-preference-option flex w-full cursor-pointer items-start gap-3 rounded-xl border border-solid border-[#D9D9D9] bg-white px-3 py-3.5 text-left transition-colors hover:border-[#BDA9EE] hover:bg-[#FBF9FF] ${
                    isSaving ? 'cursor-wait opacity-60' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="time-entry-creation-preference"
                    value={option.value}
                    defaultChecked={option.value === 'time'}
                    disabled={isSaving}
                    onChange={() => {
                      selectedViewRef.current = option.value
                    }}
                    className="mt-0.5 size-5 shrink-0 cursor-pointer accent-[#865BDF]"
                  />
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-1.5 text-sm leading-5 text-[#333]">
                      <span className="font-semibold">{option.title}</span>
                      <span className="font-normal text-[#666]">
                        {option.recommendation}
                      </span>
                    </span>
                    <span className="mt-1 block text-xs leading-[18px] text-[#666]">
                      {option.summary}
                    </span>
                    <span className="mt-1 block text-xs leading-[18px] text-[#666]">
                      {option.description}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>

          {error && (
            <p role="alert" className="mb-0 mt-3 text-xs text-[#B3261E]">
              {error}
            </p>
          )}
        </div>

        <div className="heartbeat-footer">
          <button
            type="button"
            disabled={isSaving}
            onClick={() => onSelect(selectedViewRef.current)}
            className="confirm-button primary-button min-w-[50px]"
          >
            {isSaving ? 'Saving…' : 'Ok'}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
